import type { Civility } from "../types/supabase.types.gen";

export const getFirstName = function (fullName: string) {
  return fullName.split(" ")[0];
};

export const getLastName = function (fullName: string) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [firstName, ...otherParticules] = fullName.split(" ");

  return otherParticules.join(" ");
};

export type People = {
  first_name?: string | null;
  last_name?: string | null;
  temp_first_name?: string | null;
  temp_last_name?: string | null;
  auth_user?: {
    first_name?: string | null;
    last_name?: string | null;
  } | null;
  civility?: Civility | null;
  job_title?: string | null;
};

/**
 * Resolves a person's real name from the fields that actually carry one,
 * mirroring getFullName's priority order. Deliberately does not fall back to
 * parsing the email address: getFirstName/getLastName split on spaces, so an
 * email (no space) yields the whole address as the first name and an empty
 * last name — which the API then rejects.
 */
export const resolveKnownName = function (people: People): { firstName?: string; lastName?: string } {
  const firstName = people.auth_user?.first_name || people.first_name || people.temp_first_name || undefined;
  const lastName = people.auth_user?.last_name || people.last_name || people.temp_last_name || undefined;

  return { firstName, lastName };
};

export const hasName = function (people: People) {
  if (people.first_name || people.last_name) return true;
  if (people.temp_first_name || people.temp_last_name) return true;
  if (people.auth_user?.first_name || people.auth_user?.last_name) return true;
  return false;
};

export const getFullName = function (
  people: People,
  params: { capitalizeLastName: boolean } = { capitalizeLastName: false }
) {
  const firstName = people.auth_user?.first_name ?? people.first_name ?? people.temp_first_name;
  let lastName = people.auth_user?.last_name ?? people.last_name ?? people.temp_last_name;

  if (!firstName && !lastName && people.civility !== "entity") return "‼️ Non renseigné";
  if (people.civility === "entity") return `Entité${people.job_title ? ` : ${people.job_title}` : ""}`;

  lastName = params.capitalizeLastName ? (lastName ?? "").toUpperCase() : lastName;

  return [firstName, lastName].filter(Boolean).join(" ");
};

export function getInitials(people: People): string {
  if (!hasName(people)) return "";

  const particles = ["de", "du", "d'", "des", "le", "la", "les"];

  const fullName = getFullName(people)
    .split(" ")
    .filter((part) => !particles.includes(part.toLowerCase()))
    .flatMap((part) => part.split("-"))
    .map((part) => part.charAt(0))
    .join("");

  return fullName;
}
